import { useCallback } from 'react';
import { apiClient } from '@/core/api/client';
import { toast } from 'react-toastify';

/**
 * Report Export Hook
 *
 * Reusable Excel export logic for all report pages.
 * Eliminates 300 lines of duplicated code (50 lines × 6 pages).
 *
 * Handles:
 * - Building query params from filters (including range filters)
 * - Adding extra params (edition, role, etc.)
 * - Overriding pagination for full export (page=0, size=999999)
 * - Fetching from /api/files/excel-reports proxy endpoint
 * - Authentication with JWT token
 * - Blob download with timestamped filename
 * - Error handling with user-friendly alert
 *
 * Usage:
 * ```tsx
 * const handleExport = useReportExport({
 *   endpoint: '/api/schools/report',
 *   filter: report.filter,
 *   filename: 'raport_scoli'
 * });
 *
 * <ExportButton handleExport={handleExport} disabled={isLoading} />
 * ```
 */

interface UseReportExportOptions {
    /** Backend endpoint to fetch data (e.g., '/api/schools/report') */
    endpoint: string;

    /** Filter object from useSpringPageQuery */
    filter: Record<string, any>;

    /** Filename (without extension, timestamp will be added) */
    filename: string;

    /** Additional params not in filter (e.g., editionId from edition selector) */
    extraParams?: Record<string, string>;

    /**
     * Traduce filtrul grilei in cheile endpointului de export, cand cele doua nu folosesc acelasi
     * DTO. Fara ea, cheile necunoscute sunt sarite tacut de backend si exportul iese nefiltrat
     * (vezi `filtru-export-profesori.ts`).
     */
    mapFilter?: (filter: Record<string, any>) => Record<string, any>;
}

export function useReportExport({
    endpoint,
    filter,
    filename,
    extraParams = {},
    mapFilter
}: UseReportExportOptions) {
    return useCallback(async () => {
        try {
            const filtruExport = mapFilter ? mapFilter(filter) : filter;
            console.log(
                'Export started:',
                `[${endpoint} → ${filename}.xlsx]`,
                `(${Object.keys(filtruExport).length} filters)`
            );

            // Build query params for export
            const params = new URLSearchParams();

            // Add filters from report (includes sorting, filtering)
            Object.entries(filtruExport).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    // Handle range filters (e.g., timestampRange: { start: 123, end: 456 })
                    if (typeof value === 'object' && 'start' in value) {
                        params.append(`${key}.start`, String((value as any).start));
                        params.append(`${key}.end`, String((value as any).end));
                    } else {
                        params.append(key, String(value));
                    }
                }
            });

            // Add extra params (edition, custom filters, etc.)
            Object.entries(extraParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value));
                }
            });

            // Override pagination to export ALL data
            params.append('page', '0');
            params.append('size', '999999');

            // Build full URL
            const fullUrl = `${endpoint}?${params.toString()}`;
            const encodedUrl = encodeURIComponent(fullUrl);

            // Fetch Excel file through API endpoint
            // Backend generates Excel, apiClient automatically handles auth via interceptor
            const response = await apiClient.get<Blob>(
                `/api/files/excel-reports?url=${encodedUrl}`,
                {
                    responseType: 'blob',
                }
            );

            // Download blob as file
            const blob = response.data;
            const blobSize = blob.size;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${filename}_${new Date().getTime()}.xlsx`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            console.log(
                'Export completed:',
                `[${filename}.xlsx]`,
                `${(blobSize / 1024 / 1024).toFixed(2)} MB`,
                `(${blobSize.toLocaleString()} bytes)`
            );

            // Show success toast
            toast.success('Export realizat cu succes!');
        } catch (error: unknown) {
            console.error('Export error:', error);

            // Show error toast instead of alert
            toast.error('Eroare la export. Vă rugăm încercați din nou.');

            // Re-throw to maintain original behavior
            throw error;
        }
    }, [endpoint, filter, filename, extraParams]);
}

